import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { setDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { UserContext } from '../providers/UserProvider';
import useAppMaintenance, {
  appMaintenanceStatusRef
} from '../hooks/useAppMaintenance';
import NoMatch from './NoMatch';

const WEEKDAYS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' }
];

const hourLabel12h = (h) => {
  if (h === 0) return '12 AM';
  if (h < 12) return `${h} AM`;
  if (h === 12) return '12 PM';
  return `${h - 12} PM`;
};

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => ({
  value: h,
  label: `${h}:00 (${hourLabel12h(h)})`
}));

const AppMaintenanceConfig = () => {
  document.title = 'App maintenance — SuperAdmin';
  const { user } = useContext(UserContext);
  const remote = useAppMaintenance();
  const [locked, setLocked] = useState(false);
  const [message, setMessage] = useState('');
  const [podLockEditDays, setPodLockEditDays] = useState(5);
  const [timeLockoutWeekday, setTimeLockoutWeekday] = useState(2);
  const [timeLockoutHour, setTimeLockoutHour] = useState(13);
  const [managerTimeLockoutWeekday, setManagerTimeLockoutWeekday] = useState(2);
  const [managerTimeLockoutHour, setManagerTimeLockoutHour] = useState(20);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!remote.loaded) return;
    setLocked(remote.locked);
    setMessage(remote.message);
    setPodLockEditDays(remote.podLockEditDays);
    setTimeLockoutWeekday(remote.timeLockoutWeekday);
    setTimeLockoutHour(remote.timeLockoutHour);
    setManagerTimeLockoutWeekday(remote.managerTimeLockoutWeekday);
    setManagerTimeLockoutHour(remote.managerTimeLockoutHour);
  }, [
    remote.loaded,
    remote.locked,
    remote.message,
    remote.podLockEditDays,
    remote.timeLockoutWeekday,
    remote.timeLockoutHour,
    remote.managerTimeLockoutWeekday,
    remote.managerTimeLockoutHour
  ]);

  if (!user?.SuperAdmin) {
    return <NoMatch />;
  }

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(
        appMaintenanceStatusRef,
        {
          locked,
          message: message.trim(),
          podLockEditDays: Number(podLockEditDays),
          timeLockoutWeekday: Number(timeLockoutWeekday),
          timeLockoutHour: Number(timeLockoutHour),
          managerTimeLockoutWeekday: Number(managerTimeLockoutWeekday),
          managerTimeLockoutHour: Number(managerTimeLockoutHour)
        },
        { merge: true }
      );
      toast.success('Settings saved.');
    } catch (err) {
      toast.error(err?.message || 'Could not save settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 2, maxWidth: 720, mx: 'auto' }}>
      <Typography variant='h4' gutterBottom>
        App maintenance
      </Typography>
      <Typography variant='body2' color='text.secondary' paragraph>
        SuperAdmin only. Firestore document{' '}
        <Typography component='span' variant='body2' sx={{ fontFamily: 'monospace' }}>
          AppMaintenance/status
        </Typography>{' '}
        controls the full-app lock screen, POD date lockout, and weekly time-entry lockout
        (same rules as{' '}
        <Typography component='span' variant='body2' sx={{ fontFamily: 'monospace' }}>
          checkLockout.js
        </Typography>
        ).
      </Typography>
      <Paper variant='outlined' sx={{ p: 2 }}>
        {!remote.loaded ? (
          <Box display='flex' justifyContent='center' py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant='subtitle1' fontWeight={600} gutterBottom>
              Full-app maintenance
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={locked}
                  onChange={(e) => setLocked(e.target.checked)}
                  color='primary'
                />
              }
              label='Lock application for non-SuperAdmin users'
            />
            <TextField
              fullWidth
              multiline
              minRows={4}
              margin='normal'
              label='Message shown on lock screen'
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder='Describe maintenance or expected availability…'
            />

            <Divider sx={{ my: 3 }} />

            <Typography variant='subtitle1' fontWeight={600} gutterBottom>
              POD lockout (non-admins)
            </Typography>
            <Typography variant='body2' color='text.secondary' paragraph>
              Users without admin cannot change tasks on or before the date that is this many
              full calendar days before today (older tasks are read-only).
            </Typography>
            <TextField
              type='number'
              inputProps={{ min: 0, max: 365, step: 1 }}
              label='Days before today POD becomes locked'
              value={podLockEditDays}
              onChange={(e) => setPodLockEditDays(e.target.value)}
              fullWidth
              margin='dense'
            />

            <Divider sx={{ my: 3 }} />

            <Typography variant='subtitle1' fontWeight={600} gutterBottom>
              Time entry weekly lockout
            </Typography>
            <Typography variant='body2' color='text.secondary' paragraph>
              Uses the viewer&apos;s local timezone. On the chosen weekday, after the chosen
              hour, the app switches which past week&apos;s time is still editable (same logic as
              before, now configurable).
            </Typography>
            <FormControl fullWidth margin='dense'>
              <InputLabel id='time-lockout-weekday'>Weekday lockout starts</InputLabel>
              <Select
                labelId='time-lockout-weekday'
                label='Weekday lockout starts'
                value={timeLockoutWeekday}
                onChange={(e) => setTimeLockoutWeekday(Number(e.target.value))}
              >
                {WEEKDAYS.map((w) => (
                  <MenuItem key={w.value} value={w.value}>
                    {w.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin='dense' sx={{ mt: 2 }}>
              <InputLabel id='time-lockout-hour'>Hour (local time)</InputLabel>
              <Select
                labelId='time-lockout-hour'
                label='Hour (local time)'
                value={timeLockoutHour}
                onChange={(e) => setTimeLockoutHour(Number(e.target.value))}
              >
                {HOUR_OPTIONS.map((h) => (
                  <MenuItem key={h.value} value={h.value}>
                    {h.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider sx={{ my: 3 }} />

            <Typography variant='subtitle1' fontWeight={600} gutterBottom>
              Manager time lockout (secondary)
            </Typography>
            <Typography variant='body2' color='text.secondary' paragraph>
              Applies to users with the <strong>manager</strong> claim who are not{' '}
              <strong>admin</strong> (including DJR Time Manager). Same local-time rule as above,
              but usually set earlier or on a different weekday so managers enter the locked week
              sooner than admins. Defaults match the former Time Manager cutoff (Tuesday 8:00 PM).
            </Typography>
            <FormControl fullWidth margin='dense'>
              <InputLabel id='mgr-time-lockout-weekday'>Manager weekday</InputLabel>
              <Select
                labelId='mgr-time-lockout-weekday'
                label='Manager weekday'
                value={managerTimeLockoutWeekday}
                onChange={(e) => setManagerTimeLockoutWeekday(Number(e.target.value))}
              >
                {WEEKDAYS.map((w) => (
                  <MenuItem key={w.value} value={w.value}>
                    {w.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin='dense' sx={{ mt: 2 }}>
              <InputLabel id='mgr-time-lockout-hour'>Manager hour (local time)</InputLabel>
              <Select
                labelId='mgr-time-lockout-hour'
                label='Manager hour (local time)'
                value={managerTimeLockoutHour}
                onChange={(e) => setManagerTimeLockoutHour(Number(e.target.value))}
              >
                {HOUR_OPTIONS.map((h) => (
                  <MenuItem key={h.value} value={h.value}>
                    {h.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ mt: 3 }}>
              <Button
                variant='contained'
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save to Firestore'}
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default AppMaintenanceConfig;
